import React from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import tw, { styled } from 'twin.macro'
import PropTypes from 'prop-types'

const Section = styled.section`
	${tw`flex flex-col items-center w-full pt-0 pb-12 px-4 bg-white text-center -mt-2 md:-mt-4`}
`

const Heading = styled.h2`
	${tw`text-black font-black text-center mb-3`}
	font-weight: 800;
	font-size: clamp(1.4rem, 3vw, 2.8rem);
	font-family: Flexo, system-ui, sans-serif !important;
`

const Subheading = styled.p`
	${tw`text-black text-center max-w-4xl mb-6 md:mb-8 font-semibold`}
	font-size: clamp(0.875rem, 2vw, 1rem);
	line-height: 1.6;
`

const TileGrid = styled.div`
	${tw`flex flex-col md:flex-row gap-4 w-full`}
	max-width: 1100px;
`

const Tile = styled.div`
	${tw`relative flex flex-col overflow-hidden flex-1`}
	border-radius: 1.25rem;
`

const TileImage = styled.img`
	${tw`w-full block`}
	height: auto;
`

const TileContent = styled.div`
	${tw`absolute bottom-0 left-0 right-0 flex justify-start`}
	padding: 1.75rem;
`

const TileButton = styled(Link)`
	${tw`font-semibold px-5 py-2 cursor-pointer inline-flex items-center gap-2`}
	background: transparent;
	color: white;
	border: 2px solid white;
	border-radius: 9999px;
	font-size: 0.85rem;
	text-decoration: none;

	&::after {
		content: '→';
	}
`

const ExploreThreeTile = ({ locale }) => {
	const data = useStaticQuery(ExploreThreeTileQuery)
	const mineralSwim = data.MineralSwimBanner?.nodes[0]
	const ecoSwim = data.EcoSwimBanner?.nodes[0]
	const ecoclear = data.EcoclearBanner?.nodes[0]

	return (
		<Section>
			<Heading>Water Treatment</Heading>
			<Subheading>
				Keep your pool water crystal clear and perfectly balanced with our range
				of water treatment solutions designed for healthier, easier pool maintenance.
			</Subheading>
			<TileGrid>
				<Tile>
					<TileImage src={mineralSwim?.backgroundImage?.fluid?.src} alt="Mineral Swim" />
					<TileContent>
						<TileButton to={'/' + mineralSwim?.mainActionUrl}>{mineralSwim?.mainActionText}</TileButton>
					</TileContent>
				</Tile>
				<Tile>
					<TileImage src={ecoSwim?.backgroundImage?.fluid?.src} alt="Eco Swim" />
					<TileContent>
						<TileButton to={'/' + ecoSwim?.mainActionUrl}>{ecoSwim?.mainActionText}</TileButton>
					</TileContent>
				</Tile>
				<Tile>
					<TileImage src={ecoclear?.backgroundImage?.fluid?.src} alt="Ecoclear" />
					<TileContent>
						<TileButton to={'/' + ecoclear?.mainActionUrl}>{ecoclear?.mainActionText}</TileButton>
					</TileContent>
				</Tile>
			</TileGrid>
		</Section>
	)
}

const ExploreThreeTileQuery = graphql`
	query ExploreThreeTileQuery {
		MineralSwimBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "5NNTvsh5ZYV963ovGBs3aG" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}

		EcoSwimBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "5aAKPHe2GfJKRmn5CO2A4F" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}

		EcoclearBanner: allContentfulBanner(
			filter: { contentful_id: { eq: "5FlVZhmTxfdx8cbgLHoxEw" }, node_locale: { eq: "en-AU" } }
		) {
			nodes {
				backgroundImage {
					fluid(maxWidth: 1920, quality: 80) {
						...GatsbyContentfulFluid_withWebp
					}
				}
				mainActionText
				mainActionUrl
				hook
			}
		}
	}
`

ExploreThreeTile.propTypes = {
	locale: PropTypes.string.isRequired,
}

export default ExploreThreeTile
